import { useCallback, useRef } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { SPRING } from "../lib/motion";

/**
 * Magnetic pull toward the pointer, for one or two focal controls per screen.
 *
 * Pull is clamped to `strength` (0.28 of the offset by default) so the target
 * never leaves its own hit box — the click target stays where it looks.
 */
function Magnetic({ children, strength = 0.28, className = "", ...rest }) {
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, SPRING.magnet);
  const springY = useSpring(y, SPRING.magnet);

  const onPointerMove = useCallback(
    (event) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      x.set((event.clientX - rect.left - rect.width / 2) * strength);
      y.set((event.clientY - rect.top - rect.height / 2) * strength);
    },
    [strength, x, y]
  );

  const reset = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  if (prefersReducedMotion) {
    return (
      <div ref={ref} className={className} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ x: springX, y: springY, willChange: "transform" }}
      onPointerMove={onPointerMove}
      onPointerLeave={reset}
      // Focus arriving by keyboard must not leave the control offset.
      onBlur={reset}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export default Magnetic;
